import React, { useState } from 'react';
import 'twin.macro';
import Table from 'components/Table';
import { Checkbox } from 'components/Form/Checkboxes/Checkbox';
import { EleveNameList } from 'containers/CahierJournal/EventModal/ModalBody/Ateliers/EleveNameList';
import tw, { styled } from 'twin.macro';
import { PencilIcon } from '@heroicons/react/24/outline';
import Button from 'components/Buttons/Button';

import { EleveGroup } from 'types/eleveGroups';
import { Eleve } from 'types/eleves';
import { useTranslation } from 'react-i18next';
import { bgColors } from 'components/colors';

interface Props {
  groupIds: string[];
  onChange: (groups: string[]) => void;
  groups: EleveGroup[];
  onEditGroup?: (group: string) => void;
  eleves?: Eleve[];
}

export const TableStyleWrapper = styled(Table)`
  td {
    ${tw`py-1`}
  }

  td:nth-child(1),
  th:nth-child(1) {
    ${tw`w-0.5 pl-2`}
  }

  td:nth-child(2),
  th:nth-child(2) {
    width: auto;
    max-width: 8rem;
  }

  td:nth-child(4),
  th:nth-child(4) {
    ${tw`w-auto px-0`}
  }
`;

const ColorCircle = styled.div`
  ${tw`inline-block rounded-full w-3 h-3 mr-2`}
`;

export function GroupTable(props: Props) {
  const { onChange, groups, onEditGroup, groupIds } = props;
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
  const { t } = useTranslation(['cahierJournal']);

  const toggleGroup = (group: string) => {
    setExpandedGroups(prev =>
      prev.includes(group) ? prev.filter(g => g !== group) : [...prev, group],
    );
  };

  return (
    <TableStyleWrapper condensed>
      <Table.Thead>
        <Table.Tr>
          <Table.Th />
          <Table.Th>
            {t('cahierJournal:ateliers.elevesGroup.table.group')}
          </Table.Th>
          <Table.Th>
            {t('cahierJournal:ateliers.elevesGroup.table.students')}
          </Table.Th>
          <Table.Th />
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {groups.map(group => {
          const isExpanded = expandedGroups.includes(group.id);
          const isNAGroup = group.isEphemeral || group.name === 'N/A';

          return (
            <React.Fragment key={group.id}>
              <Table.Tr>
                <Table.Td>
                  <Checkbox
                    checked={groupIds.includes(group.id) || false}
                    onChange={e => {
                      const newGroups = e.target.checked
                        ? [...groupIds, group.id]
                        : groupIds.filter(g => g !== group.id);
                      onChange(newGroups);
                    }}
                  />
                </Table.Td>
                <Table.Td>
                  <div tw="flex items-center">
                    <ColorCircle
                      css={
                        isNAGroup
                          ? tw`bg-neutral-moderate`
                          : bgColors(group.color)
                      }
                    />
                    <span css={isNAGroup && tw`text-neutral-intense italic`}>
                      {isNAGroup ? 'N/A' : group.name}
                    </span>
                  </div>
                </Table.Td>
                <Table.Td>
                  <div tw="w-full">
                    <EleveNameList
                      eleves={group.eleves}
                      groupId={group.id}
                      isExpanded={isExpanded}
                      onToggle={toggleGroup}
                    />
                  </div>
                </Table.Td>
                <Table.Td>
                  <Button
                    icon={PencilIcon}
                    iconSize="sm"
                    small
                    minimal
                    onClick={() => onEditGroup?.(group.id)}
                  />
                </Table.Td>
              </Table.Tr>
              {isExpanded &&
                group.eleves.map(eleve => (
                  <Table.Tr key={eleve.id}>
                    <Table.Td />
                    <Table.Td />
                    <Table.Td tw="pl-6">
                      <span tw="truncate block">
                        {eleve.givenName} {eleve.familyName}
                      </span>
                    </Table.Td>
                    <Table.Td />
                  </Table.Tr>
                ))}
            </React.Fragment>
          );
        })}
      </Table.Tbody>
    </TableStyleWrapper>
  );
}
